import { useCallback, useEffect, useMemo, useState } from 'react';
import { userApi } from '../services/api';

const authorProfileImageCache = new Map();
const authorProfileImageInflight = new Map();

const normalizeImage = (value) => (typeof value === 'string' ? value.trim() : '');

const unique = (items) => Array.from(new Set(items.filter(Boolean)));

const resolveAuthorProfileImage = async (author) => {
  if (!author) return '';
  if (authorProfileImageCache.has(author)) {
    return authorProfileImageCache.get(author) || '';
  }

  const inflight = authorProfileImageInflight.get(author) || userApi
    .getProfile(author)
    .then((response) => {
      const image = normalizeImage(response?.user?.profile?.profileImage);
      authorProfileImageCache.set(author, image);
      return image;
    })
    .catch(() => {
      authorProfileImageCache.set(author, '');
      return '';
    })
    .finally(() => {
      authorProfileImageInflight.delete(author);
    });

  authorProfileImageInflight.set(author, inflight);
  return inflight;
};

const useAuthorAvatar = ({ author, profileImage }) => {
  const [resolvedAuthorProfileImage, setResolvedAuthorProfileImage] = useState('');

  useEffect(() => {
    let cancelled = false;
    const directProfileImage = normalizeImage(profileImage);

    if (!author || directProfileImage) {
      setResolvedAuthorProfileImage('');
      return () => {
        cancelled = true;
      };
    }

    resolveAuthorProfileImage(author).then((image) => {
      if (!cancelled) {
        setResolvedAuthorProfileImage(image || '');
      }
    });

    return () => {
      cancelled = true;
    };
  }, [author, profileImage]);

  const avatarSources = useMemo(() => {
    if (!author) return [];
    const encodedAuthor = encodeURIComponent(author);
    const preferred = normalizeImage(profileImage) || resolvedAuthorProfileImage;

    return unique([
      preferred,
      `https://images.hive.blog/u/${encodedAuthor}/avatar`,
      `https://ui-avatars.com/api/?name=${encodedAuthor}&background=random`
    ]);
  }, [author, profileImage, resolvedAuthorProfileImage]);

  const handleAvatarError = useCallback((event) => {
    const image = event.currentTarget;
    const currentIndex = Number(image.dataset.avatarFallbackIndex || '0');
    const nextIndex = currentIndex + 1;

    if (nextIndex < avatarSources.length) {
      image.dataset.avatarFallbackIndex = String(nextIndex);
      image.src = avatarSources[nextIndex];
      return;
    }

    image.onerror = null;
  }, [avatarSources]);

  return {
    avatarSources,
    handleAvatarError
  };
};

export default useAuthorAvatar;
